import React, { useMemo, useState } from 'react';
import { EToasterStatus, showToast } from 'utils/showToast';
import Button from 'components/Button/Button';
import Input from 'components/Input/Input';
import { TableView } from 'components/TableView/TableView';
import {
  PROMO_CODES_TABLE_COLUMNS,
  PromoCodeTableRow
} from '../../constants/promoCodes';
import { CouponFormValues } from '../../types';
import InfoBanner from 'components/ui/InfoBanner';
import { localStorageUtil } from 'utils/localStorageUtil';
import { ELocalStorageKeys } from 'constants/enums';
import { useParams } from 'react-router-dom';
import { cn } from 'components/lib/utils';

interface CouponsPromoCodesProps {
  couponFormValues: CouponFormValues;
  handleBlur: (e: React.FocusEvent<HTMLInputElement>) => void;
  setFieldValue: (field: string, value: any) => void;
  promoCode: string;
  setPromoCode: (value: string) => void;
  maxRedemptions: string | number;
  setMaxRedemptions: (value: number) => void;
  isPromoCodeActive: any;
}

export default function CouponsPromoCodes({
  couponFormValues,
  handleBlur,
  setFieldValue,
  promoCode,
  setPromoCode,
  maxRedemptions,
  setMaxRedemptions,
  isPromoCodeActive
}: CouponsPromoCodesProps) {
  const { couponId } = useParams<{ couponId?: string }>();

  const couponInfoBannerKey = `${ELocalStorageKeys.COUPONS_PROMO_CODES_INFO_BANNER_CLOSED}-${couponId ?? 'new'}`;

  const [isInfoBannerEnabled, setIsInfoBannerEnabled] = useState(() => {
    const savedState = localStorageUtil.get(
      couponInfoBannerKey as ELocalStorageKeys
    );
    return savedState === null;
  });

  const hasMoreThan100PromoCodes = couponFormValues.promoCodes.length > 99;
  const shouldShowInfoBanner = hasMoreThan100PromoCodes && isInfoBannerEnabled;

  const handleCloseInfoBanner = () => {
    setIsInfoBannerEnabled(false);
    localStorageUtil.set(couponInfoBannerKey as ELocalStorageKeys, 'true');
  };

  const handleCancel = (name: string) => {
    const newPromoCodes = couponFormValues.promoCodes.filter(
      (promoCode: any) => promoCode.name !== name
    );
    setFieldValue('promoCodes', newPromoCodes);
  };

  const handleToggleActive = (
    name: string,
    currentActive: boolean,
    originalActiveState?: boolean
  ) => {
    const newPromoCodes = couponFormValues.promoCodes.map((promoCode: any) => {
      if (promoCode.name === name) {
        const newActiveState = !currentActive;
        const isStatusChanging =
          newActiveState !== (originalActiveState ?? currentActive);
        return {
          ...promoCode,
          active: newActiveState,
          isStatusChanging,
          originalActiveState: promoCode.originalActiveState ?? promoCode.active
        };
      }
      return promoCode;
    });
    setFieldValue('promoCodes', newPromoCodes);
  };

  const columns = useMemo(
    () =>
      PROMO_CODES_TABLE_COLUMNS({
        onCancel: handleCancel,
        onToggleActive: handleToggleActive
      }),
    [couponFormValues.promoCodes]
  );

  const onAddPromoCode = () => {
    const trimmedPromoCode = promoCode.trimEnd();
    if (!trimmedPromoCode || +maxRedemptions === 0) return;

    // Check if promo code name already exists in the current list
    const isDuplicate = couponFormValues.promoCodes.some(
      (existingCode) =>
        existingCode.name.toLowerCase() === trimmedPromoCode.toLowerCase()
    );

    if (isDuplicate) {
      showToast({
        message: `Promo code "${trimmedPromoCode}" already exists in this coupon`,
        status: EToasterStatus.ERROR
      });
      return;
    }

    isPromoCodeActive.mutate(trimmedPromoCode, {
      onSuccess: (isPromoCodeActiveResponse: {
        isPromoCodeActive: boolean;
        couponName?: string;
      }) => {
        const { isPromoCodeActive, couponName } = isPromoCodeActiveResponse;
        if (isPromoCodeActive === false) {
          const newPromoCodes = [
            {
              active: true,
              name: trimmedPromoCode,
              maxRedemptions: maxRedemptions,
              promoCodeAvailability: [
                {
                  redemptions: 0,
                  maxRedemptions: maxRedemptions
                }
              ],
              createdAt: new Date().toISOString(),
              fresh: true
            },
            ...couponFormValues.promoCodes
          ];
          setFieldValue('promoCodes', newPromoCodes);
          setPromoCode('');
          setMaxRedemptions(0);
        } else {
          showToast({
            message: `${promoCode} is currently active on coupon: ${couponName}`,
            status: EToasterStatus.ERROR
          });
        }
      },
      onError: () => {
        showToast({
          message: `Failed to add promo code: ${trimmedPromoCode} `,
          status: EToasterStatus.ERROR
        });
      }
    });
  };

  return (
    <div className="flex flex-col">
      <div className="flex gap-[8px]">
        <div className="flex gap-[18px]">
          <Input
            label="Promo Code Name"
            name="promoCodeName"
            value={promoCode}
            onChange={(e) => setPromoCode(e.target.value)}
            onBlur={handleBlur}
            dataTestId="promo-code-name-input"
            className="w-[265px]"
          />
          <Input
            label="Redemption Limit"
            name="redemptionLimit"
            value={maxRedemptions}
            onChange={(e) => {
              const raw = e.target.value;
              if (raw === '') {
                setMaxRedemptions(0);
                return;
              }
              const parsed = Math.floor(Number(raw));
              if (Number.isNaN(parsed)) return;
              setMaxRedemptions(parsed);
            }}
            onBlur={handleBlur}
            type="number"
            min={1}
            step={1}
            inputMode="numeric"
            pattern="[0-9]*"
            dataTestId="total-promo-code-redemptions-input"
            className="w-[265px]"
          />
        </div>
        <div className="flex items-end">
          <Button
            disabled={
              !promoCode || +maxRedemptions === 0 || isPromoCodeActive.isLoading
            }
            isLoading={isPromoCodeActive.isLoading}
            onClick={onAddPromoCode}
            className="min-w-[77px] whitespace-nowrap bg-indigo-700 hover:bg-indigo-800"
          >
            Add New
          </Button>
        </div>
      </div>
      {shouldShowInfoBanner && (
        <>
          <div className="mt-4 mb-0"></div>
          <InfoBanner
            onClose={handleCloseInfoBanner}
            message="Only the first 100 promo codes are displayed here. To retrieve all promo codes for this coupon, use the Coupons API."
          />
        </>
      )}
      {couponFormValues.promoCodes?.length > 0 && (
        <TableView<PromoCodeTableRow>
          data={couponFormValues.promoCodes}
          columns={columns}
          isLoading={false}
          rowIdIdentifier="name"
          containerClassName={cn(
            'min-h-0 max-h-[400px]',
            shouldShowInfoBanner ? '' : 'mt-[46px]'
          )}
          wrapperClassName="border-0 p-0"
          showPagination={false}
        />
      )}
    </div>
  );
}
